import React, { useEffect, useRef } from "react";
import { TextInput as ChatInput } from "../components/TextInput";
import { SendButton } from "../components/SendButton";
import { RHPNoMessages } from "./RHPNoMessages";
import { ESource, Message } from "../components/Message/Message";
import { ChatCategory, ChatMessage, Conversation } from "services/ChatApi/types";
import { ENetworkRequestStatus } from "services/BackendApi";
import { ChatMode, IToEditProps } from "../hooks/useChatState";
import { PinIcon, PlannerLogo, checkCustom, closeCustom } from "ui/Icons";
import { theme } from "../../../../tailwind.config";
import { PencilIcon } from "ui/Icons/components/Pencil.component";
import TextInput from "pureUi/TextInput";
import { CircleIconButton } from "ui/CircleIconButton";
import { SvgIcon } from "ui/SvgIcon";
import TrashIcon from 'ui/Icons/trash-no-circle.component.svg';
import downloadChatIcon from 'ui/Icons/download.chat.component.svg';

export interface IRightHandPanel {
  message: string;
  messages: ChatMessage[];
  categories: ChatCategory[];
  prompts: string[];
  chatMode: ChatMode;
  selectedCategory: ChatCategory | null
  isPromptSelected: boolean;
  selectedChat: Conversation | undefined;
  editingChatName: IToEditProps | null;
  handleCurrentMessage: (val: string) => void;
  handlePressEnter: (event: any) => void;
  selectCategory: (category: ChatCategory) => void;
  selectPrompt: (prompt: string) => void;
  pinChat: (pin: boolean, conversationId: string) => void;
  backToCategories: () => void;
  sendMessage: () => void;
  selectChatToDelete: (conversationId: string) => void;
  handleEditChatName: (newName: string) => void;
  handleSetChatToEdit: (editObj: IToEditProps | null) => void;
  renameChat: () => void;
  downloadChat: () => void;
  handleRateMessage: (messageId: string, rating: boolean) => void;
}

export const RightHandPanel = (props: IRightHandPanel) => {
  const {
    message,
    messages,
    categories,
    prompts,
    chatMode,
    selectedCategory,
    isPromptSelected,
    selectedChat,
    editingChatName,
    handleCurrentMessage,
    handlePressEnter,
    backToCategories,
    selectCategory,
    selectPrompt,
    sendMessage,
    pinChat,
    selectChatToDelete,
    handleEditChatName,
    renameChat,
    handleSetChatToEdit,
    downloadChat,
    handleRateMessage,
  } = props;
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (endRef.current) {
      endRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  return (
    <div className="chat-ongoing h-full w-full flex flex-col items-center">
      <div className="flex-1 w-full overflow-y-auto">
        {
          chatMode !== ChatMode.NORMAL ? (
            <RHPNoMessages
              categories={categories}
              prompts={prompts}
              selectCategory={selectCategory}
              chatMode={chatMode}
              selectPrompt={selectPrompt}
              selectedCategory={selectedCategory}
              backToCategories={backToCategories}
              isPromptSelected={isPromptSelected}
            />
          ) : (
            <div className="flex flex-col items-center h-full gap-30px w-full">
              <div className="flex justify-between items-center w-full font-pt-sans text-17px font-bold mr-auto h-[39px]">
                {selectedChat && editingChatName && editingChatName.fromRHP ?
                  (
                    <div className="flex items-center gap-20px w-full h-full">
                      <TextInput
                        className="flex-1 ml-3px"
                        placeholder="Search Chats..."
                        inputClassName="bg-white-hard !p-[9px] m- placeholder:!text-gray-80 placeholder:italic placeholder:text-15px placeholder:!capitalize"
                        id="chat-search"
                        value={editingChatName.newName}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleEditChatName(e.target.value)}
                      />
                      <div className="flex items-center gap-10px h-full">
                        <CircleIconButton
                          type="secondary"
                          className="min-w-[20px] max-w-[20px] h-[20px]"
                          iconClass="inline relative left-0 top-0 w-[10px] h-[10px]"
                          iconSrc={closeCustom}
                          onClick={() => handleSetChatToEdit(null)}
                        />
                        <CircleIconButton
                          type="primary"
                          className="min-w-[20px] max-w-[20px] h-[20px] bg-brown-prime"
                          iconClass="inline relative left-0 top-0 w-[10px] h-[10px]"
                          iconSrc={checkCustom}
                          onClick={renameChat}
                        />
                      </div>
                    </div>
                  ) : (selectedChat ? (
                    <>
                      {selectedChat.name}
                      <div className="flex items-baseline gap-10px">
                        <PencilIcon
                          className="cursor-pointer"
                          width={17}
                          onClick={() => handleSetChatToEdit({ conversationId: selectedChat.conversationId, fromRHP: true, newName: selectedChat.name })}
                        />
                        <SvgIcon
                          IconComponent={TrashIcon}
                          className="w-[15px] h-[15px] cursor-pointer"
                          defaultFill={theme.colors['brown-prime']}
                          onClick={() => selectChatToDelete(selectedChat.conversationId)}
                        />
                        <PinIcon
                          onClick={() => pinChat(!selectedChat.isPinned, selectedChat.conversationId)}
                          className="cursor-pointer"
                          stroke={theme.colors['brown-prime']}
                          fill={theme.colors[selectedChat.isPinned ? 'brown-prime' : 'white']}
                        />
                        <SvgIcon
                          IconComponent={downloadChatIcon}
                          className="w-[15px] h-[15px] cursor-pointer"
                          defaultFill={theme.colors['brown-prime']}
                          onClick={downloadChat}
                        />
                      </div>
                    </>
                  ) : ('New Chat Name'))
                }
              </div>
              <div className="w-full overflow-y-auto">
                {
                  messages.length > 0 ? (
                    messages.map((msg, index) => (
                      <>
                        <Message
                          key={`message-${index}`}
                          text={msg.message}
                          date={new Date(msg.timestamp)}
                          source={ESource.USER}
                          status={ENetworkRequestStatus.SUCCESS}
                          rating={msg.rating}
                          messageId={msg.messageId}
                          handleRateMessage={handleRateMessage}
                        />
                        <Message
                          key={`response-${index}`}
                          text={msg.response}
                          date={new Date(msg.timestamp)}
                          source={ESource.CHATBOT}
                          status={msg.response ? ENetworkRequestStatus.SUCCESS : ENetworkRequestStatus.PENDING}
                          rating={msg.rating}
                          messageId={msg.messageId}
                          handleRateMessage={handleRateMessage}
                        />
                      </>
                    ))
                  ) : (
                    <div className="flex flex-col items-center h-full gap-30px">
                      <PlannerLogo icon='gray-60' />
                      <div className="font-pt-sans text-17px text-gray-100 text-center">
                        <div>No messages yet</div>
                        <div>Start a conversation by typing a message below.</div>
                      </div>
                    </div>
                  )
                }
                <div ref={endRef} />
              </div>
            </div>
          )
        }
      </div>
      <div className="w-full flex flex-col justify-end">
        <div className="flex items-center gap-20px mt-5px">
          <ChatInput
            fieldId="chat-input"
            className='uppercase w-full latter tracking-[0px]'
            value={message}
            inputClassName='min-h-[50px] bg-white-hard placeholder:text-gray-100 placeholder:italic placeholder:text-15px placeholder:font-light placeholder:font-pt-sans placeholder:tracking-[0px] content-center'
            onChange={handleCurrentMessage}
            placeholder='Ask anything'
            onKeyUp={handlePressEnter}
            type="textarea"
          />
          <SendButton disabled={message.length === 0} onClick={sendMessage} />
        </div>
      </div>
    </div>
  );
};